import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Mail, Phone, MapPin } from 'lucide-react';
import { formatDate } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerProfile = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomerData();
  }, [customerId]);

  const fetchCustomerData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [customerRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/customers/${customerId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/invoices?customer_id=${customerId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setCustomer(customerRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      toast.error('Failed to fetch customer data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 md:p-12">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-8 md:p-12">
        <p className="text-muted-foreground">Customer not found</p>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      unpaid: 'bg-amber-100 text-amber-700 border-amber-200',
      overdue: 'bg-red-100 text-red-700 border-red-200',
      cancelled: 'bg-gray-200 text-gray-600 border-gray-300',
      proforma: 'bg-blue-100 text-blue-700 border-blue-200',
    };
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide border ${styles[status] || ''}`}>
        {status}
      </span>
    );
  };

  const totalPaid = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total, 0);
  const totalUnpaid = invoices.filter(inv => inv.status === 'unpaid').reduce((sum, inv) => sum + inv.total, 0);

  return (
    <div className="p-8 md:p-12" data-testid="customer-profile">
      <Button
        variant="ghost"
        onClick={() => navigate('/customers')}
        className="mb-6 gap-2"
        data-testid="back-to-customers"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Customers
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
        <Card className="lg:col-span-1 p-8 border-border/50">
          <div className="flex items-center justify-center mb-6">
            <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-3xl font-bold text-primary">
                {customer.name.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-center mb-2">{customer.name}</h2>
          {customer.company && (
            <p className="text-center text-muted-foreground mb-6">{customer.company}</p>
          )}
          <div className="space-y-4">
            {customer.email && (
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{customer.email}</span>
              </div>
            )}
            {customer.phone && (
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{customer.phone}</span>
              </div>
            )}
            {customer.address && (
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span>{customer.address}</span>
              </div>
            )}
            <div className="pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground mb-1">Preferred Currency</p>
              <p className="text-xl font-bold text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {customer.currency}
              </p>
            </div>
          </div>
        </Card>

        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <Card className="p-8 border-border/50">
              <p className="text-sm text-muted-foreground mb-2">Total Paid</p>
              <h3 className="text-3xl font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {customer.currency} {totalPaid.toFixed(2)}
              </h3>
            </Card>
            <Card className="p-8 border-border/50">
              <p className="text-sm text-muted-foreground mb-2">Total Unpaid</p>
              <h3 className="text-3xl font-bold text-amber-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {customer.currency} {totalUnpaid.toFixed(2)}
              </h3>
            </Card>
          </div>

          <Card className="p-8 border-border/50">
            <h3 className="text-2xl font-bold mb-6">Invoice History</h3>
            {invoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No invoices yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between p-4 bg-muted rounded-lg hover:bg-accent cursor-pointer transition-colors"
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                    data-testid={`invoice-${invoice.id}`}
                  >
                    <div>
                      <p className="font-semibold">{invoice.invoice_number}</p>
                      <p className="text-sm text-muted-foreground">{formatDate(invoice.invoice_date)}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {customer.currency} {invoice.total.toFixed(2)}
                      </p>
                      {getStatusBadge(invoice.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;